import { createRemoteJWKSet, decodeJwt, jwtVerify } from "jose";

import { env } from "~/env";

import { getCookie, QA_AUTH_TOKEN_COOKIE } from "./cookie";
import { isOriginAllowed, parseEmbedAuthPayload } from "./embed-utils";
import { logger } from "./logger";

export interface Tenant {
  id: string;
  name: string;
  roles: string[];
}

export interface User {
  id: string;
  email: string;
  teams: Tenant[];
}

interface TokenData {
  iss: string;
  aud: string;
  sub: string;
  iat: number;
  exp: number;
  org_id: string;
  org_name: string;
  ident_provenance: {
    email: string;
  };
  teams: Tenant[];
}

const JWKS = createRemoteJWKSet(
  new URL("/.well-known/jwks.json", env.VITE_IDENTITY_SERVICE_URL),
);

// Cryptographic verification — for newly-received tokens (OAuth, refresh, embed).
async function verifyToken(token: string): Promise<TokenData> {
  const { payload } = await jwtVerify<TokenData>(token, JWKS);
  return payload;
}

// Decode-only — for tokens already verified and stored in localStorage.
// Avoids a JWKS network call on every page load; expiry is checked separately.
function decodeToken(token: string): TokenData {
  return decodeJwt<TokenData>(token);
}

const ACCCESS_TOKEN_REFRESH_ADVANCE = 0.5 * 60 * 1000; // 30 seconds

const TOKEN_REFRESH_URL = new URL(
  "/refresh/",
  env.VITE_IDENTITY_SERVICE_URL,
).toString();
const TOKEN_EXCHANGE_URL = new URL(
  "/token/",
  env.VITE_IDENTITY_SERVICE_URL,
).toString();
const LOGIN_URL = new URL("/login/", env.VITE_IDENTITY_SERVICE_URL).toString();
const LOGOUT_URL = new URL(
  "/logout/",
  env.VITE_IDENTITY_SERVICE_URL,
).toString();

const TOKEN_EXCHANGE_PATH = "/auth/callback";
const REDIRECT_AFTER_AUTH_CALLBACK_PATH = "/";

const STORAGE_PREFIX = "INVISIBLE_AUTH";
const REFRESH_TOKEN_STORAGE_KEY = `${STORAGE_PREFIX}_refreshToken`;
const ACCESS_TOKEN_STORAGE_KEY = `${STORAGE_PREFIX}_accessToken`;
const ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY = `${STORAGE_PREFIX}_accessTokenExpiresAt`;
const POST_LOGIN_REDIRECT_STORAGE_KEY = `${STORAGE_PREFIX}_postLoginRedirect`;

const QA_TENANTS: Tenant[] = [
  {
    id: "qa-tenant-admin",
    name: "[QA] Admin Tenant",
    roles: ["Atomic:Admin"],
  },
  {
    id: "qa-tenant-builder-1",
    name: "[QA] Builder Tenant 1",
    roles: ["Atomic:Builder"],
  },
  {
    id: "qa-tenant-builder-2",
    name: "[QA] Builder Tenant 2",
    roles: ["Atomic:Builder"],
  },
];

class AuthService {
  private _isQaMode = false;
  private _isInitialized = false;
  private _isAuthenticated = false;
  private _user: User | null = null;
  private _error: Error | null = null;
  private accessToken: string | null = null;
  private accessTokenExpiresAt: number | null = null;
  private listeners: Set<() => void> = new Set();

  // Promises used for avoiding race conditions
  private initPromise: Promise<void> | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  private get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  private set refreshToken(refreshToken: string | null) {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
  }

  async ensureInitialized(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.initialize();
    }

    return this.initPromise;
  }

  private async initialize(): Promise<void> {
    try {
      // Check for QA auth token first
      const qaToken = getCookie(QA_AUTH_TOKEN_COOKIE);
      if (qaToken) {
        this._isQaMode = true;
        this._isAuthenticated = true;
        this.accessToken = qaToken;
        this._user = {
          id: "qa-user",
          email: "qa@test.example.com",
          teams: QA_TENANTS,
        };
        return;
      }

      const isCallbackRoute =
        window.location.pathname === TOKEN_EXCHANGE_PATH ||
        window.location.pathname === `/${TOKEN_EXCHANGE_PATH}`;

      if (isCallbackRoute) {
        await this.handleOAuthCallback();
        return;
      }

      const refreshToken = this.refreshToken;
      if (refreshToken) {
        // Restore the cached access token from localStorage before calling getAccessToken().
        // Without this, this.accessToken is always null on a fresh page load, so getAccessToken()
        // always falls through to a network refresh call — even when the token from the previous
        // session is still valid. Restoring it here lets the validity check in getAccessToken()
        // short-circuit and skip the /refresh/ roundtrip (~230ms) on most page loads.
        this.restoreAccessTokenFromStorage();
        await this.getAccessToken();
      }
    } catch (error) {
      this._error = error as Error;
      console.error("Auth initialization failed:", error);
    } finally {
      this._isInitialized = true;
      this.notifyListeners();
    }
  }

  private async handleOAuthCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    if (!code) {
      throw new Error("No code found in callback URL");
    }

    try {
      const res = await fetch(TOKEN_EXCHANGE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });

      if (!res.ok) {
        throw new Error("Failed to exchange code for tokens");
      }

      await this.processTokenResponse(res);

      const postLoginRedirect = localStorage.getItem(
        POST_LOGIN_REDIRECT_STORAGE_KEY,
      );
      localStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);

      window.location.href =
        postLoginRedirect || REDIRECT_AFTER_AUTH_CALLBACK_PATH;
    } catch (error) {
      this._error = error as Error;
      throw error;
    }
  }

  async getAccessToken(): Promise<string | null> {
    // In QA mode, always return the QA token
    if (this._isQaMode) {
      return this.accessToken;
    }

    const accessTokenValid =
      this.accessToken &&
      this.accessTokenExpiresAt &&
      Date.now() < this.accessTokenExpiresAt - ACCCESS_TOKEN_REFRESH_ADVANCE;
    if (accessTokenValid) {
      return this.accessToken;
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.refreshAccessToken();
    const newAccessToken = await this.refreshPromise;
    this.refreshPromise = null;

    return newAccessToken;
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.refreshToken;

    if (!refreshToken) {
      this.clearState();
      this.redirectToLogin();
      return null;
    }

    try {
      const res = await fetch(TOKEN_REFRESH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (res.status >= 400 && res.status < 500) {
        this.clearState();
        this.redirectToLogin();
        return null;
      }

      if (!res.ok) {
        throw new Error("Failed to refresh token");
      }

      const { accessToken } = await this.processTokenResponse(res);
      return accessToken;
    } catch (error) {
      this._error = error as Error;
      this.clearState();
      console.error("Token refresh failed:", error);
      return null;
    }
  }

  private async applyTokens(
    accessToken: string,
    refreshToken?: string,
  ): Promise<void> {
    const decoded = await verifyToken(accessToken);
    const expiresAt = (decoded.exp ?? 0) * 1000;

    this._isAuthenticated = true;
    this._user = this.tokenToUser(decoded);
    this.accessToken = accessToken;
    this.accessTokenExpiresAt = expiresAt;

    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    localStorage.setItem(
      ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY,
      String(expiresAt),
    );

    if (refreshToken !== undefined) this.refreshToken = refreshToken;

    this.notifyListeners();
  }

  private async processTokenResponse(
    res: Response,
  ): Promise<{ accessToken: string }> {
    const json: { access_token: string; refresh_token: string } =
      await res.json();
    await this.applyTokens(json.access_token, json.refresh_token);
    return { accessToken: json.access_token };
  }

  private tokenToUser(tokenData: TokenData): User {
    return {
      id: tokenData.sub,
      email: tokenData.ident_provenance.email,
      teams: tokenData.teams,
    };
  }

  private restoreAccessTokenFromStorage(): void {
    const cachedToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    const cachedExpiresAt = localStorage.getItem(
      ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY,
    );

    if (!cachedToken || !cachedExpiresAt) return;

    const expiresAt = Number(cachedExpiresAt);
    const isValid = Date.now() < expiresAt - ACCCESS_TOKEN_REFRESH_ADVANCE;
    if (!isValid) return;

    try {
      const decoded = decodeToken(cachedToken);
      this.accessToken = cachedToken;
      this.accessTokenExpiresAt = expiresAt;
      this._isAuthenticated = true;
      this._user = this.tokenToUser(decoded);
    } catch {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY);
    }
  }

  getLoginUrl(): string | null {
    if (
      window.location.pathname === TOKEN_EXCHANGE_PATH ||
      window.location.pathname === `/${TOKEN_EXCHANGE_PATH}`
    ) {
      return null;
    }

    // Save current location for post-login redirect
    localStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, window.location.href);

    const redirectUri = encodeURIComponent(
      `${window.location.origin}${TOKEN_EXCHANGE_PATH}`,
    );

    return `${LOGIN_URL}?redirect_uri=${redirectUri}`;
  }

  redirectToLogin(): void {
    const loginUrl = this.getLoginUrl();
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  }

  logout(): void {
    this.clearState();
    this.notifyListeners();

    // Hit logout endpoint to clear session cookies
    window.location.href = LOGOUT_URL;
  }

  private clearState(): void {
    this._isQaMode = false;
    this._isAuthenticated = false;
    this._user = null;
    this._error = null;
    this.accessToken = null;
    this.accessTokenExpiresAt = null;

    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY);

    this.refreshToken = null;
  }

  // Bypasses OAuth — used for cross-origin iframes where the embedding page
  // already holds a valid token from the shared IdP. Returns false when verification fails.
  async injectTokens({
    access_token,
    refresh_token,
  }: {
    access_token: string;
    refresh_token?: string;
  }): Promise<boolean> {
    try {
      await this.applyTokens(access_token, refresh_token);
      return true;
    } catch (error) {
      logger.error("Failed to inject auth tokens", {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  // Waits up to `timeoutMs` for an atomic:auth postMessage from the parent frame.
  // Resolves true only when tokens arrive and verification succeeds; false on timeout or failure.
  waitForEmbedToken(timeoutMs: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        window.removeEventListener("message", handler);
        resolve(false);
      }, timeoutMs);

      const handler = async (event: MessageEvent) => {
        if (!isOriginAllowed(event.origin)) return;

        const tokens = parseEmbedAuthPayload(event.data);
        if (!tokens) return;

        clearTimeout(timer);
        window.removeEventListener("message", handler);
        const injected = await this.injectTokens(tokens);
        resolve(injected);
      };

      window.addEventListener("message", handler);
    });
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  isQaMode(): boolean {
    return this._isQaMode;
  }

  isInitialized(): boolean {
    return this._isInitialized;
  }

  isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  user(): User | null {
    return this._user;
  }

  error(): Error | null {
    return this._error;
  }
}

export const authService = new AuthService();
